/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/KPISelfAssessment.tsx
 * PURPOSE: Employee KPI self-assessment page — view KPI packs, rate performance, submit assessment
 * SDLC FRAMEWORK VERSION: 6.3.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Vietnamese Multi-Industry BPM, KPI/Compensation Framework V2.4
 * - Impact: Revenue Critical — KPI-driven bonus calculation, 3-Pillar Compensation Model
 * - Stakeholders: Service Staff (NQH), Branch Managers, HR Team
 *
 * @technical-architecture
 * - Layer: Presentation Layer, Page Components
 * - Pattern: React Router, Page Layout, Component Composition
 * - Dependencies: React 18, React Router, TypeScript, apiWrapper
 *
 * @cross-references
 * - Design: docs/02-design/05-business-logic/COMPENSATION-FRAMEWORK-DESIGN.md
 * - API: backend/app/api/v2/kpi.py (KPI Self-Assessment endpoints)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ShiftSwapModern.module.css';

/* ─── Types ─────────────────────────────────────────────────────────────────── */

interface KpiItem {
  id: string;
  name_vi: string;
  name_en?: string;
  weight: number;
  target: string;
  description_vi?: string;
}

interface KpiPack {
  id: string;
  name_vi: string;
  name_en?: string;
  kpis: KpiItem[];
}

interface KpiRatingEntry {
  kpi_id: string;
  rating: string;
  comment: string;
}

interface KpiPeriodStatus {
  period_id: string;
  period_name: string;
  deadline: string;
  status: string; // DRAFT | SUBMITTED | APPROVED | REJECTED
  pack: KpiPack | null;
  ratings: KpiRatingEntry[];
  submitted_at?: string;
  approved_at?: string;
  reviewer_comment?: string;
}

/* ─── Constants ─────────────────────────────────────────────────────────────── */

const RATING_OPTIONS = [
  { value: '', label: '— Chọn đánh giá —' },
  { value: 'FEE', label: 'Vượt trội (FEE)', multiplier: 1.5 },
  { value: 'EE', label: 'Xuất sắc (EE)', multiplier: 1.2 },
  { value: 'ME', label: 'Đạt yêu cầu (ME)', multiplier: 1.0 },
  { value: 'PME', label: 'Cần cải thiện (PME)', multiplier: 0.7 },
  { value: 'DNME', label: 'Không đạt (DNME)', multiplier: 0.0 },
];

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Bản nháp',
  SUBMITTED: 'Đã gửi',
  APPROVED: 'Đã duyệt',
  REJECTED: 'Từ chối',
};

const STATUS_COLORS: Record<string, string> = {
  DRAFT: '#eab308',
  SUBMITTED: '#3b82f6',
  APPROVED: '#22c55e',
  REJECTED: '#ef4444',
};

/* ─── Auth helper ───────────────────────────────────────────────────────────── */

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    const token = localStorage.getItem('access_token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const raw = localStorage.getItem('auth-storage');
    if (raw) {
      const parsed = JSON.parse(raw);
      const tenantKey = parsed?.state?.tenantKey || parsed?.state?.tenant_key;
      if (tenantKey) headers['X-Tenant-Key'] = tenantKey;
    }
  } catch { /* ignore */ }
  return headers;
}

/* ─── Component ─────────────────────────────────────────────────────────────── */

export const KPISelfAssessment: React.FC = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [periodStatus, setPeriodStatus] = useState<KpiPeriodStatus | null>(null);
  const [ratings, setRatings] = useState<Record<string, string>>({});
  const [comments, setComments] = useState<Record<string, string>>({});

  const isReadOnly = periodStatus?.status === 'SUBMITTED' || periodStatus?.status === 'APPROVED';

  /* ── Load current period KPI status ─────────────────────────────────────── */

  const loadCurrentKpi = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/v2/kpi/employee/me/current', {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data: KpiPeriodStatus = await res.json();
        setPeriodStatus(data);

        // Pre-populate ratings and comments from existing data
        const ratingMap: Record<string, string> = {};
        const commentMap: Record<string, string> = {};
        if (data.ratings && Array.isArray(data.ratings)) {
          for (const entry of data.ratings) {
            ratingMap[entry.kpi_id] = entry.rating || '';
            commentMap[entry.kpi_id] = entry.comment || '';
          }
        }
        setRatings(ratingMap);
        setComments(commentMap);
      } else {
        const data = await res.json().catch(() => ({}));
        const errMsg =
          (typeof data?.detail === 'string' ? data.detail : data?.detail?.message_vi) ||
          data?.message_vi ||
          'Không thể tải dữ liệu KPI.';
        setError(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
      }
    } catch {
      setError('Lỗi kết nối. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCurrentKpi();
  }, [loadCurrentKpi]);

  /* ── Rating / comment handlers ──────────────────────────────────────────── */

  const handleRatingChange = (kpiId: string, value: string) => {
    setRatings(prev => ({ ...prev, [kpiId]: value }));
  };

  const handleCommentChange = (kpiId: string, value: string) => {
    setComments(prev => ({ ...prev, [kpiId]: value }));
  };

  /* ── Build payload ──────────────────────────────────────────────────────── */

  const buildPayload = () => {
    const kpis = periodStatus?.pack?.kpis || [];
    return {
      period_id: periodStatus?.period_id,
      ratings: kpis.map(kpi => ({
        kpi_id: kpi.id,
        rating: ratings[kpi.id] || '',
        comment: comments[kpi.id] || '',
      })),
    };
  };

  /* ── Save draft ─────────────────────────────────────────────────────────── */

  const handleSaveDraft = async () => {
    setSaving(true);
    setError(null);
    setMessage(null);

    try {
      const res = await fetch('/api/v2/kpi/employee/me/submit', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ...buildPayload(), is_draft: true }),
      });

      if (res.ok) {
        setMessage('Đã lưu nháp thành công.');
        await loadCurrentKpi();
      } else {
        const data = await res.json().catch(() => ({}));
        const rawDetail = data?.detail;
        const errMsg =
          (typeof rawDetail === 'string' ? rawDetail : rawDetail?.message_vi || rawDetail?.message) ||
          data?.message_vi ||
          'Không thể lưu nháp.';
        setError(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
      }
    } catch {
      setError('Lỗi kết nối. Vui lòng thử lại.');
    } finally {
      setSaving(false);
    }
  };

  /* ── Submit assessment ──────────────────────────────────────────────────── */

  const handleSubmit = async () => {
    // Validate all KPIs have ratings
    const kpis = periodStatus?.pack?.kpis || [];
    const missing = kpis.filter(kpi => !ratings[kpi.id]);
    if (missing.length > 0) {
      setError(`Vui lòng đánh giá tất cả KPI. Còn ${missing.length} KPI chưa được đánh giá.`);
      return;
    }

    // Confirmation dialog
    const confirmed = window.confirm(
      'Bạn có chắc chắn muốn gửi đánh giá? Sau khi gửi, bạn sẽ không thể chỉnh sửa.'
    );
    if (!confirmed) return;

    setSubmitting(true);
    setError(null);
    setMessage(null);

    try {
      const res = await fetch('/api/v2/kpi/employee/me/submit', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ...buildPayload(), is_draft: false }),
      });

      if (res.ok) {
        setMessage('Đánh giá KPI đã được gửi thành công.');
        await loadCurrentKpi();
      } else {
        const data = await res.json().catch(() => ({}));
        const rawDetail = data?.detail;
        const errMsg =
          (typeof rawDetail === 'string' ? rawDetail : rawDetail?.message_vi || rawDetail?.message) ||
          data?.message_vi ||
          'Không thể gửi đánh giá.';
        setError(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
      }
    } catch {
      setError('Lỗi kết nối. Vui lòng thử lại.');
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Render helpers ─────────────────────────────────────────────────────── */

  const renderStatusBadge = (status: string) => {
    const color = STATUS_COLORS[status] || '#6b7280';
    return (
      <span
        className={styles.statusBadge}
        data-status={status}
        data-testid="kpi-status-badge"
        style={{ backgroundColor: color, color: '#fff', padding: '4px 12px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 600 }}
      >
        {STATUS_LABELS[status] || status}
      </span>
    );
  };

  const formatDeadline = (deadline: string | undefined): string => {
    if (!deadline) return '';
    try {
      return new Date(deadline).toLocaleDateString('vi-VN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    } catch {
      return deadline;
    }
  };

  /* ── Render ─────────────────────────────────────────────────────────────── */

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <h1 id="kpi-assessment-title" className={styles.title}>KPI Tự Đánh Giá</h1>
          <button onClick={() => navigate('/dashboard')} className={styles.backButton}>
            Về Dashboard
          </button>
        </div>
      </header>

      <main aria-labelledby="kpi-assessment-title" className={styles.main}>
        {/* Status messages */}
        {message && (
          <p
            className={styles.statusSuccess}
            data-testid="kpi-success-msg"
            role="alert"
            aria-live="polite"
          >
            {message}
          </p>
        )}
        {error && (
          <p className={styles.statusError} data-testid="kpi-error-msg">{error}</p>
        )}

        {/* Loading state */}
        {loading && (
          <div className={styles.card}>
            <p className={styles.cardDescription}>Đang tải dữ liệu KPI...</p>
          </div>
        )}

        {/* No data state */}
        {!loading && !periodStatus && !error && (
          <div className={styles.card}>
            <p className={styles.cardDescription}>
              Không có kỳ đánh giá KPI nào đang mở. Vui lòng liên hệ quản lý.
            </p>
          </div>
        )}

        {/* Overview section */}
        {!loading && periodStatus && (
          <>
            <div className={styles.card}>
              <h2 className={styles.cardTitle}>Thông tin kỳ đánh giá</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span><strong>Kỳ đánh giá:</strong> {periodStatus.period_name}</span>
                  {renderStatusBadge(periodStatus.status)}
                </div>
                <div>
                  <strong>Hạn nộp:</strong> {formatDeadline(periodStatus.deadline)}
                </div>
                {periodStatus.submitted_at && (
                  <div>
                    <strong>Ngày gửi:</strong> {formatDeadline(periodStatus.submitted_at)}
                  </div>
                )}
                {periodStatus.approved_at && (
                  <div>
                    <strong>Ngày duyệt:</strong> {formatDeadline(periodStatus.approved_at)}
                  </div>
                )}
                {periodStatus.reviewer_comment && (
                  <div>
                    <strong>Nhận xét của quản lý:</strong> {periodStatus.reviewer_comment}
                  </div>
                )}
                {periodStatus.pack && (
                  <div>
                    <strong>Gói KPI:</strong> {periodStatus.pack.name_vi}
                  </div>
                )}
              </div>
            </div>

            {/* KPI list grouped by pack */}
            {periodStatus.pack && periodStatus.pack.kpis.length > 0 && (
              <div className={styles.card}>
                <h2 className={styles.cardTitle}>
                  Danh sách KPI — {periodStatus.pack.name_vi}
                </h2>
                <ul className={styles.requestList}>
                  {periodStatus.pack.kpis.map(kpi => (
                    <li
                      key={kpi.id}
                      className={styles.requestItem}
                      data-testid={`kpi-item-${kpi.id}`}
                    >
                      <div className={styles.requestHeader}>
                        <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                          {kpi.name_vi}
                        </span>
                        <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                          Trọng số: {kpi.weight}%
                        </span>
                      </div>

                      {kpi.description_vi && (
                        <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '4px 0' }}>
                          {kpi.description_vi}
                        </p>
                      )}

                      <p style={{ fontSize: '0.85rem', margin: '4px 0' }}>
                        <strong>Mục tiêu:</strong> {kpi.target}
                      </p>

                      {/* Rating dropdown */}
                      <div className={styles.formGroup} style={{ marginTop: '8px' }}>
                        <label
                          htmlFor={`rating-${kpi.id}`}
                          className={styles.label}
                        >
                          Đánh giá
                        </label>
                        <select
                          id={`rating-${kpi.id}`}
                          value={ratings[kpi.id] || ''}
                          onChange={e => handleRatingChange(kpi.id, e.target.value)}
                          disabled={isReadOnly}
                          className={styles.input}
                          data-testid={`kpi-rating-${kpi.id}`}
                        >
                          {RATING_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Comment textarea */}
                      <div className={styles.formGroup} style={{ marginTop: '8px' }}>
                        <label
                          htmlFor={`comment-${kpi.id}`}
                          className={styles.label}
                        >
                          Nhận xét
                        </label>
                        <textarea
                          id={`comment-${kpi.id}`}
                          value={comments[kpi.id] || ''}
                          onChange={e => handleCommentChange(kpi.id, e.target.value)}
                          disabled={isReadOnly}
                          placeholder="Nhập nhận xét cho KPI này"
                          maxLength={1000}
                          className={styles.textarea}
                          data-testid={`kpi-comment-${kpi.id}`}
                          rows={2}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Action buttons */}
            {!isReadOnly && periodStatus.pack && (
              <div className={styles.actionsRow}>
                <button
                  onClick={handleSaveDraft}
                  disabled={saving}
                  className={styles.secondaryButton}
                  data-testid="kpi-save-draft"
                >
                  {saving ? 'Đang lưu...' : 'Lưu nháp'}
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className={styles.primaryButton}
                  data-testid="kpi-submit"
                >
                  {submitting ? 'Đang gửi...' : 'Gửi đánh giá'}
                </button>
              </div>
            )}

            {/* Read-only notice */}
            {isReadOnly && (
              <div className={styles.card}>
                <p className={styles.cardDescription}>
                  {periodStatus.status === 'SUBMITTED'
                    ? 'Đánh giá đã được gửi. Đang chờ quản lý duyệt.'
                    : 'Đánh giá đã được duyệt. Không thể chỉnh sửa.'}
                </p>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default KPISelfAssessment;
